import { API, Auth } from "aws-amplify";
import { datadogLogs } from "@datadog/browser-logs";
import { CurrencyCode } from "../API";

// Save-time FX resolution, mirroring the app (globals.dart saveTransaction,
// ~line 339). The app is the spec: a transaction stores the rate that applied
// on its own paidAt, so the books reconcile to the receipt rather than to
// whenever the row happened to be saved.
//
// Until !411 the web had no endpoint to ask — the FastAPI on ocr.expensly.ch
// had no CORS middleware, so the browser could not reach it at all. Rows were
// written with null FX and the readers fall back to the raw foreign amount
// (see 86c9pkyzj), which books EUR 100 as CHF 100.

// BAZG quotes everything against CHF and nothing else, so the rate fetched is
// always X→CHF and the divide inverts it for a CHF transaction against a
// foreign base. Unreachable while the web pins baseCurrency to CHF — a CHF
// transaction equals the base and returns before any fetch — but the app
// derives baseCurrency per bookkeeping, so keep the branch faithful to it.
export const getBaseCurrencyAmount = (
  currencyCode: CurrencyCode,
  amount: number | null,
  exchangeRate: number | null
): number | null =>
  amount != null && exchangeRate != null
    ? parseFloat(
        (currencyCode === CurrencyCode.chf
          ? amount / exchangeRate
          : amount * exchangeRate
        ).toFixed(2)
      )
    : null;

export type Fx = {
  exchangeRate: number | null;
  baseCurrencyAmount: number | null;
  // A rate was needed and none came back. 86 of the schema's 160 currency
  // codes are simply not quoted by BAZG, so this is expected, not exceptional.
  // The row still saves — matching the app, which also stores null — and the
  // caller warns rather than blocking the user.
  rateMissing: boolean;
  // The lookup itself failed (endpoint down, timeout, expired session) as
  // opposed to BAZG having no rate. Both end with no rate, but only this one
  // means "we don't know" — an update must not overwrite FX it already has on
  // the strength of a failed request. Kept apart from rateMissing because an
  // unquoted currency is a real answer and should be written.
  lookupFailed: boolean;
};

const NO_FX: Fx = {
  exchangeRate: null,
  baseCurrencyAmount: null,
  rateMissing: false,
  lookupFailed: false,
};

// Returns null for a currency BAZG does not quote. Never returns the amount
// itself — a caller that falls back to the raw foreign value books it as CHF.
export const fetchExchangeRate = async (
  fromCurrency: CurrencyCode,
  paidAt: string
): Promise<number | null> => {
  const rate = await API.get("expenslyREST", "/exchange-rate", {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    queryStringParameters: {
      from_currency: fromCurrency.toUpperCase(),
      datetime: paidAt,
    },
  });
  // The Lambda answers with a bare number, or null when unquoted. Anything
  // else is a malformed response, not a rate.
  return typeof rate === "number" && Number.isFinite(rate) ? rate : null;
};

// A captured FX pair describes exactly one (currency, amount, paidAt): the rate
// is a function of currency and date, and the amount scales it into
// baseCurrencyAmount. Change any of the three and the stored pair describes a
// row that no longer exists.
//
// This only decides what happens when the lookup FAILS — a successful resolve
// always wins. Getting it wrong is quiet: miss paidAt and a user correcting a
// receipt's date keeps the old day's rate, with no error anywhere.
export const isFxCaptureStale = (
  captured: { currencyCode: CurrencyCode; amount: number; paidAt: string },
  next: { currencyCode: CurrencyCode; amount: number; paidAt: string }
): boolean =>
  captured.currencyCode !== next.currencyCode ||
  captured.amount !== next.amount ||
  captured.paidAt !== next.paidAt;

// paidAt is the receipt's date (YYYY-MM-DD), never today's — the rate that
// applied when the expense happened is the one the books need.
export const resolveFx = async (
  currencyCode: CurrencyCode,
  baseCurrency: CurrencyCode,
  amount: number,
  paidAt: string
): Promise<Fx> => {
  if (currencyCode === baseCurrency) return NO_FX;

  let exchangeRate: number | null = null;
  try {
    // Mirrors globals.dart:354 — ask for the *foreign* side of the pair, since
    // BAZG only quotes against CHF. With a CHF transaction and a foreign base
    // that is the base's rate, and getBaseCurrencyAmount divides by it.
    // Fetching chf→chf instead would return 1 and book the amount unconverted.
    exchangeRate = await fetchExchangeRate(
      currencyCode === CurrencyCode.chf ? baseCurrency : currencyCode,
      paidAt
    );
  } catch (error) {
    // An outage and an unquoted currency both end with no rate, but only one of
    // them is an answer — see Fx.lookupFailed.
    datadogLogs.logger.warn("Exchange rate lookup failed", {
      currencyCode,
      paidAt,
      error: error instanceof Error ? error.message : String(error),
    });
    return {
      exchangeRate: null,
      baseCurrencyAmount: null,
      rateMissing: true,
      lookupFailed: true,
    };
  }

  if (exchangeRate == null) {
    return {
      exchangeRate: null,
      baseCurrencyAmount: null,
      rateMissing: true,
      lookupFailed: false,
    };
  }

  return {
    exchangeRate,
    baseCurrencyAmount: getBaseCurrencyAmount(
      currencyCode,
      amount,
      exchangeRate
    ),
    rateMissing: false,
    lookupFailed: false,
  };
};
